import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CarouselProvider, Slider, Slide, DotGroup, ButtonNext } from 'pure-react-carousel';

import { ReactComponent as CloseBtn } from '../../../assets/images/graphics/btn-close.svg';
import { getImageFilepath } from '../../../models/Utils';
import { useSwipeEvent } from '../../../models/Hooks';
import { timelineSlideTypes, linkTypes, timelineAnchorPosition, swipeDirection, swipeType } from '../../../models/Models';
import VideoSlide from '../VideoSlide/VideoSlide';
import Object3DSlide from '../Object3DSlide/Object3DSlide';
import './TimelineCarousel.scss';
import { Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { ADAARIARoles } from '../../../models/ADA';

const TimelineCarousel = ({ card, isActive, inLightbox, onExploreClicked, onExitClicked }) => {
  const isLandscape = useMediaQuery({
    query: '(orientation: landscape)',
  });
  const carouselRef = useRef(null);
  const controlRef = useRef(null);
  const configData = useSelector(state => state.config);
  const [exploreMode, setExploreMode] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselContext = controlRef.current && controlRef.current.context;
  const menuOpened = useSelector(state => state.data.menuOpened);

  // effect to handle horizental swipe on desktop
  const swipeHandler = useCallback(
    e => {
      if (!e) {
        return;
      }
      if (
        e.dir === swipeDirection.RIGHT &&
        e.origin === swipeType.SCROLL &&
        currentSlide < carouselContext.state.totalSlides - 1
      ) {
        carouselContext.setStoreState({
          currentSlide: currentSlide + 1,
        });
      }
      if (e.dir === swipeDirection.LEFT && swipeType.SCROLL && currentSlide > 0) {
        carouselContext.setStoreState({
          currentSlide: currentSlide - 1,
        });
      }
    },
    [carouselContext, currentSlide]
  );

  useSwipeEvent(carouselRef.current, swipeHandler);

  useEffect(() => {
    if (!carouselContext) {
      return;
    }
    const onChange = () => setCurrentSlide(carouselContext.state.currentSlide);
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext]);

  function getLink(slide) {
    if (
      slide &&
      slide.Link &&
      slide.Link.HasLink &&
      slide.Link.Text &&
      slide.Link.Text.length > 0
    ) {
      var isExternal = slide.Link.Type === linkTypes.EXTERNAL;
      if (isExternal && slide.Link.ExternalURL) {
        var external = slide.Link.ExternalURL;
        return (
          <a href={external.Text} target="_blank" rel="noopener noreferrer">
            {slide.Link.text}
          </a>
        );
      } else if (!isExternal && slide.Link.InternalURL) {
        var external = slide.Link.InternalURL;
        if (external.BasePath && external.BasePath.Text && external.Path && external.Path.Path) {
          return (
            <Link to={`/${external.BasePath.Text}/${external.Path.Path}`}>{slide.Link.text}</Link>
          );
        }
      }
    }
    return null;
  }

  function getBackgroundImagePosition(anchor) {
    if (anchor) {
      switch (anchor) {
        case timelineAnchorPosition.CENTER:
          return 'center';
        case timelineAnchorPosition.RIGHT:
          return 'right';
        case timelineAnchorPosition.LEFT:
          return 'left';
      }
    }
    return 'center';
  }

  const on3DExploreSelected = () => {
    if(isLandscape){
      setExploreMode(true);
    } else {
      onExploreClicked(card.Year);
    }
  };

  const on3DExploreClosed = () => {
    if(isLandscape){
      setExploreMode(false);
    }
  };

  const onCloseButtonClicked = () => {
    onExitClicked();
    setExploreMode(false);
  };

  useEffect(() => {
    setExploreMode(inLightbox);
  }, [inLightbox]);

  useEffect(() => {
    if(isLandscape && exploreMode){
      setExploreMode(false);
    }
  }, [currentSlide]);

  return (
    <div
      className={`simple-carousel timeline-carousel ${
        card.Cards.length &&
        card.Cards[currentSlide] &&
        ((isLandscape && card.Cards[currentSlide].IsDarkModeLandscape) ||
          (!isLandscape && card.Cards[currentSlide].IsDarkModePortrait))
          ? 'dark'
          : ''
      }`}
      ref={carouselRef}
    >
      <span role={ADAARIARoles.Heading} className="year-text ada-hidden">{card.Year}</span>
      <CarouselProvider
        totalSlides={(card.Cards || []).length}
        naturalSlideWidth="100%"
        naturalSlideHeight="100%"
        dragStep={1}
        lockOnWindowScroll={true}
        isPlaying={false}
        interval={10000}
        dragEnabled={(card.Cards || []).length > 1}
        touchEnabled={(card.Cards || []).length > 1}
      >
        <Slider classNameAnimation="carousel" moveThreshold="0.02">
          {(card.Cards || []).map((slide, i) => (
            <Slide key={slide.Key} index={i}>
              <div
                className={`card-holder ${slide.Object ? 'object-3d' : ''}`}
                aria-label={
                  slide.BackgroundImage && slide.BackgroundImage.Image
                    ? slide.BackgroundImage.AltText
                    : ''
                }
                style={
                  slide.Type === timelineSlideTypes.IMAGE &&
                  slide.BackgroundImage &&
                  slide.BackgroundImage.Image
                    ? {
                        backgroundImage: `url("${
                          configData.basePath +
                          configData.apiMediaPath +
                          getImageFilepath(slide.BackgroundImage.Image)
                        }")`,
                        backgroundPositionX: getBackgroundImagePosition(slide.ImageAnchor),
                      }
                    : {}
                }
              >
                {slide.Type === timelineSlideTypes.VIDEO ? (
                  <VideoSlide
                    data={slide}
                    active={currentSlide === i && isActive}
                    closeButton={true}
                    showPlayButton={false}
                    forceShowControls={false}
                    canPlay={false}
                  />
                ) : null}
                {slide.Type === timelineSlideTypes.OBJECT_3D ? (
                  <Object3DSlide
                    data={slide}
                    isActive={currentSlide === i && isActive}
                    isExploreMode={exploreMode}
                    onExploreSelected={on3DExploreSelected}
                    onExploreClosed={on3DExploreClosed}
                  />
                ) : null}
                {slide.Type === timelineSlideTypes.IMAGE && slide.BackgroundImage.Overlay ? (
                  <div
                    className="overlay"
                    style={{
                      backgroundColor: slide.BackgroundImage.Overlay,
                      opacity: slide.BackgroundImage.Opacity,
                    }}
                  ></div>
                ) : null}
                <div className="content">
                  {(slide.Type !== timelineSlideTypes.OBJECT_3D) || (slide.Type === timelineSlideTypes.OBJECT_3D && (inLightbox || isLandscape)) ? 
                    <p tabIndex={isActive && currentSlide == i && !menuOpened? 0:-1} className= {slide.Type === timelineSlideTypes.OBJECT_3D ? "fadein" : ""}>{slide.Body}</p> : null
                  }
                  {getLink(slide)}
                </div>
              </div>
            </Slide>
          ))}
        </Slider>
        <div className="btn-next-container">
          {currentSlide !== (card.Cards || []).length - 1 && <ButtonNext tabIndex={isActive && !menuOpened ? 0 : -1}/>}
        </div>
        <DotGroup
          ref={controlRef}
          style={{ visibility: isActive && card.Cards.length > 1 ? 'visible' : 'hidden' }}
        />
      </CarouselProvider>

      <span className="year-text">{card.Year}</span>
      {inLightbox && !isLandscape && 
        <div className="close-btn">
          <CloseBtn onClick={onCloseButtonClicked} />
        </div>
      }
    </div>
  );
};

export default TimelineCarousel;
